<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useReserveStore } from '@/stores/reserve/reserveStore';
    import { useLanguageStore } from '@/stores/common/languageStore';

    // Vue
    import { onMounted, reactive } from 'vue';
    import { useRouter } from 'vue-router';
    import { getCurrentInstance } from 'vue';
    import { inject } from 'vue';
    import { ref } from 'vue';

    // commonProject
    import utils from '@/commonProject/helpers/utils.js';

    // ----------------------------
    // 定数
    // ----------------------------
    const router = useRouter();
    const commonStore = useCommonStore();
    const reserveStore = useReserveStore();
    const languageStore = useLanguageStore();

    // メッセージダイアログ取得
    const { messageDialog } = inject('messageDialogKey');

    // ----------------------------
    // リアクティブ変数
    // ----------------------------
    const requestData = reactive(reserveStore.rsvdtlRequest);

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    const allFlag = ref(true);

    // ----------------------------
    // 関数
    // ----------------------------
    // 戻るボタン押下時
    function onBack() {
        // リクエスト情報クリア
        reserveStore.clearRequestData();

        // 遷移元画面をリセットする。
        commonStore.setFrom('');

        // 遷移元の画面に戻る。遷移元はホーム画面。
        router.push({ name: 'home' });
    }
    // 進むボタン押下時
    async function onAdvance() {
        // 遷移元画面を「予約(プログラムからサービス選択)画面」に設定する。
        commonStore.setFrom('program');

        // サービス詳細取得APIのリクエストボディを生成する。
        const serviceDetailrequestBody = {
            Lang: languageStore.getLanguageValue,
            ServicePk: requestData.ServicePk,
        };

        // サービス詳細取得APIをコールする。
        const response = await reserveStore.getServiceDetailAction(API_URL, serviceDetailrequestBody);

        if (response.success) {
            if (!utils.isNullEmpty(reserveStore.serviceDetail.RegulationPk)) {
                // 規約PKを保持する。
                requestData.RegulationPk = reserveStore.serviceDetail.RegulationPk;
                reserveStore.setRsvdtlRequestData(requestData);

                // 予約(規定同意)画面へ遷移する。
                router.push({ name: 'reserveplanningagree' });
            } else {
                reserveStore.setRsvdtlRequestData(requestData);
                // 予約(プログラムから利用方法など選択)画面へ遷移する。
                router.push({ name: 'reserveplanningselection' });
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }
    // すべてのプログラムを押下
    async function onAllProgram() {
        const serviceRequestBody = {
            Lang: languageStore.getLanguageValue,
            SelfUseMethod: '2',
            Used3MonthAgo: false,
            AssignedProgram: false,
        };

        // サービス一覧取得API
        const response = await reserveStore.getReserveServiceAction(API_URL, serviceRequestBody);

        // エラーがある場合は、エラー処理を行い処理を終了する。
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }

        allFlag.value = true;
    }
    // 過去3ヶ月の間にご利用のプログラムを押下
    async function onLastThreeMonth() {
        const serviceRequestBody = {
            Lang: languageStore.getLanguageValue,
            SelfUseMethod: '2',
            Used3MonthAgo: true,
            AssignedProgram: false,
        };

        // 3ヶ月分のサービス一覧情報取得API
        const response = await reserveStore.getReserveServiceAction(API_URL, serviceRequestBody);

        // エラーがある場合は、エラー処理を行い処理を終了する。
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }

        allFlag.value = false;
    }

    // ----------------------------
    // ライフサイクル
    // ----------------------------
    onMounted(async () => {
        // 前画面から戻ってきたか判定する。
        if (!commonStore.reservePageBack) {
            // 遷移元画面をリセットする。
            commonStore.setFrom('');

            // 初期化
            reserveStore.clearAll();
        } else {
            // 前画面から戻ってきた場合、座標記録をクリアする。
            reserveStore.clearReserveStep();
        }

        // 前画面からの戻るフラグを初期化する。
        commonStore.reservePageBack = false;

        // 戻り先座標を記録する。
        // 予約画面のルートと、設定・選択状況に応じて、戻る画面を制御するために座標を記録する。
        // プログラムを選んで予約のルート
        reserveStore.reserveStep.program.reserveplanningprogram = true;

        // サービス一覧取得APIのリクエストボディを生成する。
        const serviceRequestBody = {
            Lang: languageStore.getLanguageValue,
            SelfUseMethod: '2',
            Used3MonthAgo: false,
            AssignedProgram: false,
        };

        // サービス一覧取得APIをコールする。
        const response = await reserveStore.getReserveServiceAction(API_URL, serviceRequestBody);

        if (response.success) {
            // 登録されているサービスが存在するか判定する。
            if (!utils.isNullEmptyArray(reserveStore.serviceList)) {
                if (reserveStore.serviceList.length === 1) {
                    // サービスが1件しかない場合

                    // サービス詳細取得APIのリクエストボディを生成する。
                    requestData.ServicePk = reserveStore.serviceList[0].ServicePk;
                    const serviceDetailRequestBody = {
                        Lang: languageStore.getLanguageValue,
                        ServicePk: reserveStore.serviceList[0].ServicePk,
                    };

                    // サービス詳細取得APIをコールする。
                    const response2 = await reserveStore.getServiceDetailAction(API_URL, serviceDetailRequestBody);

                    if (response2.success) {
                        reserveStore.setRsvdtlRequestData(requestData);
                    } else {
                        messageDialog('error', response2.errorMessage);
                        if (response2.redirect) {
                            location.href = response2.redirectUrl;
                        }
                    }
                }
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    });
</script>

<template>
    <section id="main">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3 class="reserve">
                    <span>{{ $t('reservePlanningProgram.parts.pageTitle1') }}</span>
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <img src="/img/step/mode2_step1.png" class="step" width="410" height="46" />
                    </template>
                    <template v-else>
                        <img src="/img/step/mode2_step1_en.png" class="step" width="410" height="46" />
                    </template>
                </h3>
                <section class="whiteBoxContents">
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <img src="/img/step/mode2_step1.png" class="step" width="410" height="46" />
                    </template>
                    <template v-else>
                        <img src="/img/step/mode2_step1_en.png" class="step" width="410" height="46" />
                    </template>
                    <p class="pageInfo">{{ $t('reservePlanningProgram.parts.pageInformation1') }}</p>
                    <hr class="line" style="margin-bottom: 30px" />
                    <ul class="btnArea center" id="selectProgram">
                        <li :class="{ selected: allFlag }">
                            <button type="button" id="program1" @click="onAllProgram">{{ $t('reservePlanningProgram.parts.linkButton1') }}</button>
                        </li>
                        <li :class="{ selected: !allFlag }">
                            <button type="button" id="program2" @click="onLastThreeMonth">{{ $t('reservePlanningProgram.parts.linkButton2') }}</button>
                        </li>
                    </ul>
                    <ul class="serviceList">
                        <li v-for="service in reserveStore.serviceList" :key="service.ServiceId">
                            <input type="radio" :id="'radio' + service.ServiceId" name="selectedServiceList" :value="service.ServicePk" v-model="requestData.ServicePk" />
                            <label :for="'radio' + service.ServiceId">
                                <ul class="serviceData">
                                    <li>
                                        <span>{{ service.ServiceNm }}</span>
                                    </li>
                                    <li>
                                        <span>{{ service.ServiceExp }}</span>
                                    </li>
                                </ul>
                            </label>
                        </li>
                    </ul>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <button type="button" class="btnNormal" @click="onBack">{{ $t('common.parts.backPage') }}</button>
                        </li>
                        <li>
                            <button type="button" class="btnPrimary" @click="onAdvance" :disabled="!requestData.ServicePk">{{ $t('common.parts.nextPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style></style>
